/* eslint-disable react/destructuring-assignment */
import React, { Fragment, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { usePubNub } from "pubnub-react";
import classNames from "classnames";
import { ChatService, FirebaseService, JobService } from "@services";
import utils from "@utils";
import AnalyticsService from "@services/AnalyticsService";
import { ANALYTICS_EVENTS_ACTIONS, ANALYTICS_EVENTS_CATEGORIES } from "@constants/analyticsEvents";
import Image from "../Image/Image";
import ChatIcon from "../../images/chat-icon.svg";

import "./JobChat.scss";

const JobChat = props => {
	const pubnub = usePubNub();
	const [channels, setChannels] = useState([]);
	const [messages, setMessages] = useState([]);
	const [participants, setParticipants] = useState({});
	const [input, setInput] = useState("");
	const [activeChat, setActiveChat] = useState();
	const [collapsed, setCollapsed] = useState(true);
	const [unreadChannels, setUnreadChannels] = useState([]);
	const [loadingChat, setLoadingChat] = useState(true);
	const [showMentions, setShowMentions] = useState("none");
	// State hooks used to track PubNub message and status events
	const [currentMsgEvent, setCurrentMsgEvent] = useState();
	const [currentStatusEvent, setCurrentStatusEvent] = useState();
	const [currentObjectsEvent, setCurrentObjectsEvent] = useState();
	const [mentionMessage, setMentionMessage] = useState();
	// Notification-related state hooks
	const [showNotifBanner, setShowNotifBanner] = useState(false);

	const inputRef = useRef();

	/** Checks if browser push notifications are enabled by the current user. If they are not, we display an option for them to do so every 12 hours. */
	const checkNotificationsEnabled = () => {
		FirebaseService.getNotificationsEnabled().then(enabled => {
			const lastCheck = localStorage.getItem("RKchat-ne");
			const halfDay = 1000 * 60 * 60 * 12;
			if (!enabled && (lastCheck || 0) < new Date().getTime() - halfDay) {
				setShowNotifBanner(true);
			}
		});
	};

	/** @async Enables browser push notifications for the device being used to access RollKall */
	const enableNotifications = async () => {
		const granted = await FirebaseService.requestPermission();
		if (granted) localStorage.removeItem("RKchat-ne");
		setShowNotifBanner(!granted);
	};

	/** Dismiss the reminder to enable browser push notifications */
	const dismissNotifBanner = () => {
		localStorage.setItem("RKchat-ne", new Date().getTime());
		setShowNotifBanner(false);
	};

	/**
	 * Event handler that is triggered when a key is pressed down on the chat input. This determines whether or not to show
	 * The mentions list and also provided keyboard accessible functionality when the window is open.
	 * @param {Event} event The HTML event to inspect for handling the keydown event properly
	 */
	const handleInputKeydown = event => {
		const {
			key,
			target: { value },
		} = event;
		const options = document.querySelectorAll(".mentions > div");
		const activeIndex = Array.from(options).indexOf(
			document.querySelector(".mentions > div.active"),
		);
		if (key === "ArrowDown" && activeIndex !== -1) {
			event.preventDefault();
			options[activeIndex].classList.remove("active");
			if (activeIndex === options.length - 1) options[0].classList.add("active");
			else options[activeIndex + 1].classList.add("active");
		} else if (key === "ArrowUp" && activeIndex !== -1) {
			event.preventDefault();
			options[activeIndex].classList.remove("active");
			if (activeIndex === 0) options[options.length - 1].classList.add("active");
			else options[activeIndex - 1].classList.add("active");
		} else if (key === "Enter" && activeIndex !== -1 && showMentions) {
			event.preventDefault();
			// If using '@here', only replace text after the mention with the word 'here'
			if (options[activeIndex].innerHTML.includes("@here")) {
				setInput(value.replace(/@\S*$/g, "@here"));
			} else {
				setInput(value.replace(/@\S*$/g, `@${options[activeIndex].innerHTML} `));
			}
			setShowMentions("none");
		}
	};

	/**
	 * Provides channel metadata for the specified PubNub channel. If no metadata is found for a given channel,
	 * the name property is set to an empty string. Currently used to retrieve the name of the associated job event.
	 * @param {String} channel The channel for which metadata should be retrieved
	 * @param {Boolean} append Determines whether or not to append the retrieved data to the existing list of channels. Defaults to `false`
	 */
	const fetchChannelMetadata = useCallback(
		async (channel, append = false) => {
			// Only request metadata if the provided channel does not exist in the list of channels for the component
			if (channels.findIndex(ch => ch.id === channel.replace("job-", "")) === -1) {
				try {
					const {
						data: { name },
					} = await pubnub.objects.getChannelMetadata({ channel });
					setChannels(prev => {
						if (append && !prev.some(ch => ch.id === channel.replace("job-", ""))) {
							return [...prev, { id: channel.replace("job-", ""), name }];
						}
						if (!prev.some(ch => ch.id === channel.replace("job-", ""))) {
							return [{ id: channel.replace("job-", ""), name }];
						}
						return prev;
					});
				} catch (err) {
					setChannels(prev => {
						if (append && !prev.some(ch => ch.id === channel.replace("job-", ""))) {
							return [...prev, { id: channel.replace("job-", ""), name: "" }];
						}
						if (!prev.some(ch => ch.id === channel.replace("job-", ""))) {
							return [{ id: channel.replace("job-", ""), name: "" }];
						}
						return prev;
					});
				}
			}
		},
		[channels],
	);

	/**
	 * Retrieve the user information for all potential participants for a given chat
	 * @param {String} channel The channel for which potential member info should be retrieved. This should be a JobEventId
	 */
	const fetchParticipantsInfo = channel => {
		ChatService.getParticipants(channel)
			.then(({ data }) => {
				const jobParticipants = {};
				data.forEach(({ UserId, ...item }) => {
					jobParticipants[UserId] = item;
				});
				setParticipants(jobParticipants);
			})
			.catch(() => {});
	};

	/**
	 * Send a new message to everyone in the active chat window. If it appears that a user has been mentioned,
	 * the subsequent actions are handled in a `useEffect` hook.
	 */
	const sendMessage = () => {
		pubnub
			.publish({
				channel: `job-${activeChat}`,
				message: { text: input, timestamp: new Date().getTime() },
			})
			.then(() => {
				if (input.includes("@")) setMentionMessage(input);
				setInput("");
			});
	};

	/**
	 * PubNub event handler for receiving new messages in any chat the user is subscribed to
	 * @param {Object} event The event that is returned when a new message has been detected in chat
	 */
	const handleMessage = event => {
		// New messages are handled in a useEffect hook that depends on currentMsgEvent
		setCurrentMsgEvent(event);
	};

	/**
	 * PubNub event handler for any status changes that occur.
	 * @param {Object} event The event that is returned when any status change occurs on the current PubNub instance
	 */
	const handleStatus = event => {
		// New status events are handled in a useEffect hook that depends on currentStatusEvent
		// Subscription events have a category of "PNConnectedCategory" but subscription cancellation events don't have a category
		// If the category exists and is "PNConnectedCategory" or if no category exists at all, handle the event
		if (!event.category || (event.category && event.category === "PNConnectedCategory")) {
			setCurrentStatusEvent(event);
		}
	};

	/**
	 * PubNub event handler for any objects changes that occur. This is only used to check for channel metadata updates
	 * @param {Object} event The event that is returned when any changes to objects occur on the current PubNub instance
	 */
	const handleObjects = event => {
		const { message } = event;
		if (message.event === "set" && message.type === "channel") {
			setCurrentObjectsEvent(event);
		}
	};

	/**
	 * Filters the list of job participants by the provided substring
	 * @param {String} substring The term to search for in the names of job participants
	 * @returns {Array<String>} An array of job participants whose name contains the provided substring
	 */
	const filterJobParticipants = substring =>
		Object.entries(participants).filter(([, p]) =>
			p.FullName.toLowerCase().includes(substring.toLowerCase()),
		);

	/**
	 * Exit a specified PubNub channel
	 * @param {String} channelToExit The channel that the user should be removed from as a member and subscriber. This should be a JobEventId
	 */
	const exitChannel = channelToExit => {
		try {
			const channelObject = { channels: [`job-${channelToExit}`] };
			// Unsubscribing will trigger a "PNUnsubscribeOperation" that is handled by the status listener
			pubnub.unsubscribe(channelObject);
		} catch (err) {
			// Unable to create a scenario where this condition was met.
			// All attempts to purposely mess this request up resulted in the unsubscribe function not being executed at all
		}
	};

	/**
	 * Event handler for hovering over names in a list of mentionable users
	 * @param {Event} The HTML event to inspect for handling the hover event (onmouseover) properly
	 */
	const handleMentionHover = ({ target }) => {
		const parent = target.parentElement;
		const currentActive = parent.querySelector("div.active");
		if (currentActive !== target) {
			currentActive.classList.remove("active");
			target.classList.add("active");
		}
	};

	/**
	 * Event handler for clicking a user's name from one of the messages they have sent. This adds a mention of that user to the text input
	 * @param {String} prevInput The value of the input variable prior to clicking the user's name
	 * @param {String} name The name of the user to mention in the text input
	 */
	const handleUsernameClick = (prevInput, name) => {
		setInput(`${prevInput}@${name} `);
		if (inputRef.current) inputRef.current.focus();
	};

	/**
	 * Event handler for clicking a user's name from the list of mentionable users. This adds a mention of that user to the text input.
	 * This method varies slightly from `handleUsernameClick` in that it replaces the last '@' symbol and any text after it with a mentionable user
	 * in the text input
	 * @param {String} prevInput The value of the input variable prior to clicking the user's name
	 * @param {String} name The name of the user to mention in the text input
	 */
	const handleMentionClick = (prevInput, name) => {
		setInput(prevInput.replace(/@\S*$/g, `@${name} `));
		setShowMentions("none");
	};

	/**
	 * Renders chat messages after searching for any mentioned users and emphasizing their name with bold text
	 * @param {String} text The text contents of the chat message
	 * @param {Object} chatMembers An object map of chat users and their relevant data
	 */
	const renderMessageText = (text, chatMembers) => {
		let result = text;
		// Create an array of all mentionable names for this chat
		const names = Object.values(chatMembers).map(({ FullName }) => `@${FullName}`);
		// Add the text '@here' to the list of mentionable names
		names.push("@here");
		// If the mentioned name appears in the chat text, add : on each side of the name as an indicator
		names.forEach(name => {
			if (text.includes(name)) result = result.replace(name, `__${name}__`);
		});
		// Split the current string into an array separating terms by the : character added in the previous step
		result = result.split("__");
		result.forEach((item, index) => {
			// If a mentioned name is found, replace the simple text entry with an HTML <strong> element wrapping the mentioned name
			if (names.includes(item)) result[index] = <strong>{item}</strong>;
			else result[index] = `${item}${index < result.length - 1 ? " " : ""}`;
		});
		// Remove any results from the text that are equivalent to an empty string and return the result
		result = result.filter(item => item !== "");
		return result.map((item, index) => {
			const key = `${text.replace(/\s/g, "")}_${index}`;
			return <Fragment key={key}>{item}</Fragment>;
		});
	};

	/**
	 * Event handler for when a Firebase chat notification is received by the user.
	 * This should cause the chat to open and forced to that chat immediately, while also subscribing and setting membership.
	 * @param {Object} event A Firebase notification event to be parsed and handled when it pertains to a chat message
	 */
	const handleFirebaseNotif = event => {
		const {
			detail: {
				fcmOptions: { link },
			},
		} = event;
		if (link && link.includes("#openChat")) {
			setCollapsed(false);
		}
	};

	// Effect hook that executes when component has been mounted & unmounted
	useEffect(() => {
		AnalyticsService.trackEvent({
			category: ANALYTICS_EVENTS_CATEGORIES.JOB_CHAT,
			action: ANALYTICS_EVENTS_ACTIONS.JOB_CHAT_MOUNTED,
		});
		document.addEventListener("notificationReceived", handleFirebaseNotif);
		(async () => {
			pubnub.addListener({
				status: handleStatus,
				message: handleMessage,
				objects: handleObjects,
			});
			checkNotificationsEnabled();
			try {
				const uuid = await pubnub.getUUID();
				const { data } = await pubnub.objects.getMemberships({ uuid });
				// Retrieve the channel IDs for all channels where the user is a member
				const channelsWhereMember = data.map(({ channel: { id } }) => id);
				let subscribeTo = [...channelsWhereMember];
				let unsubscribeFrom = [];

				JobService.getAll({ pageNumber: 1, displayCount: 500, status: 1 })
					.then(({ data: { ItemList } }) => {
						const activeJobs = ItemList.map(({ Id }) => `job-${Id}`);
						subscribeTo = channelsWhereMember.filter(channel =>
							activeJobs.includes(channel),
						);
						unsubscribeFrom = channelsWhereMember.filter(
							channel => !activeJobs.includes(channel),
						);
					})
					.catch(() =>
						AnalyticsService.trackEvent({
							category: ANALYTICS_EVENTS_CATEGORIES.JOB_CHAT,
							action: ANALYTICS_EVENTS_ACTIONS.JOB_CHAT_ERROR_RETRIEVING_ACTIVE_JOBS,
						}),
					)
					.finally(() => {
						// Unsubscribe from any inactive job channels and remove the user's membership for the same channels (if there are any)
						if (unsubscribeFrom.length > 0) {
							pubnub.unsubscribe({ channels: unsubscribeFrom });
							pubnub.objects.removeMemberships({ channels: unsubscribeFrom });
						}
						// Subscribe to all channels where the user is a member (if there are any)
						if (subscribeTo.length > 0) pubnub.subscribe({ channels: subscribeTo });
					});
			} catch (err) {
				// Unable to create a scenario where this condition was met.
			}
		})();
		return () => {
			pubnub.removeListener({
				status: handleStatus,
				message: handleMessage,
				objects: handleObjects,
			});
		};
	}, []);

	// Effect hook to update data when the active chat is selected or deselected whenever the collapsed state of the chat window is changed
	useEffect(() => {
		(async () => {
			if (activeChat === undefined) {
				setMessages([]);
				setInput("");
				setParticipants({});
				setLoadingChat(true);
			} else if (!collapsed) {
				// Fetch info for all other users in the chat history before setting the message history
				fetchParticipantsInfo(activeChat);
				try {
					// Fetch message history for active chat
					const { channels: retrievedChannels } = await pubnub.fetchMessages({
						channels: [`job-${activeChat}`],
					});
					if (retrievedChannels[`job-${activeChat}`]) {
						const history = retrievedChannels[`job-${activeChat}`].map(
							({ uuid: publisher, ...channel }) => ({ ...channel, publisher }),
						);
						// Set message history
						setMessages(history);
						// If the active chat had an unread message indicator, clear that chat once
						if (unreadChannels.includes(activeChat)) {
							setUnreadChannels(prev =>
								prev.filter(channel => channel !== activeChat),
							);
						}
					}
				} catch (err) {
					setMessages([
						{
							message: {
								text: "Couldn't retrieve previous messages",
								timestamp: new Date().getTime(),
								isError: true,
							},
						},
					]);
				} finally {
					setLoadingChat(false);
				}
				// Check for any updates to channel metadata while away from the chat list
				const metadataPromises = channels
					.map(({ id }) => id)
					.map((channel, i) => fetchChannelMetadata(channel, i > 0));
				await Promise.all(metadataPromises);
			}
		})();
	}, [activeChat, collapsed]);

	// Effect hook to handle PubNub message events
	useEffect(() => {
		// When there is a message to track, follow these steps
		if (currentMsgEvent) {
			const { message, channel } = currentMsgEvent;
			// Check if the user got a message in the chat that is currently open on screen
			// If so, update the messages array and display the newest message
			if (channel.replace("job-", "") === activeChat && !collapsed) {
				// ESLint rule disabled because it's a bad rule, quite frankly...
				// eslint-disable-next-line no-prototype-builtins
				if (typeof message === "string" || message.hasOwnProperty("text")) {
					setMessages(msgs => [...msgs, currentMsgEvent]);
				}
				// If not, add the channel ID (without "job-" to the list of channels with unread messages)
			} else {
				setUnreadChannels(prev => [...prev, channel.replace("job-", "")]);
			}
			// Reset the tracked message so that a new one can be listened for and processed accordingly
			setCurrentMsgEvent();
		}
	}, [currentMsgEvent]);

	// Effect hook to handle new PubNub status events
	useEffect(() => {
		if (currentStatusEvent) {
			const { operation, affectedChannels, subscribedChannels } = currentStatusEvent;
			if (operation === "PNSubscribeOperation") {
				(async () => {
					// Make sure the user's channel membership is up to date with their subscribed channels
					pubnub.objects.setMemberships({ channels: subscribedChannels });
					// Get updated metadata for subscribed channels
					const metadataPromises = subscribedChannels.map((channel, i) =>
						fetchChannelMetadata(channel, i > 0),
					);
					await Promise.all(metadataPromises);
					// If only one channel is affected, check if the related job is currently being viewed in the browser
					// If so, we are opening the page using the button on `JobDetails.js`
					if (affectedChannels.length === 1) {
						const jobEventId = affectedChannels[0].replace("job-", "");
						if (window.location.pathname.includes(jobEventId))
							setActiveChat(affectedChannels[0].replace("job-", ""));
					}
					if (collapsed) setCollapsed(false);
				})();
			} else if (operation === "PNUnsubscribeOperation") {
				// Remove the user's channel membership after they unsubscribe
				pubnub.objects.removeMemberships({ channels: affectedChannels });
				// Remove the unsubscribed channel from the list of chats shown to the user
				setChannels(prev =>
					prev.filter(channel => channel.id !== affectedChannels[0].replace("job-", "")),
				);
				// In case the user has somehow unsubscribed from a chat while it's still open, reset the active chat
				if (activeChat === affectedChannels[0].replace("job-", "")) setActiveChat();
			}

			// Wipe the status event from memory so that a new one can be listened for and processed appropriately
			setCurrentStatusEvent();
		}
	}, [currentStatusEvent]);

	// Effect hook to handle new PubNub objects events
	useEffect(() => {
		if (currentObjectsEvent) {
			const {
				message: { data },
			} = currentObjectsEvent;
			setChannels(prev =>
				prev.map(channel => {
					if (channel.id === data.id.replace("job-", ""))
						return { ...channel, name: data.name };
					return channel;
				}),
			);

			// Clear objects event from memory to continue listening for other updates that may happen
			setCurrentObjectsEvent();
		}
	}, [currentObjectsEvent]);

	// Effect hook to automatically scroll the messages to the bottom to view the most recent message when a new one is added
	// Triggered when a new message is received or when message history is loaded on the active chat
	useEffect(() => {
		if (messages.length > 0 && activeChat && !collapsed) {
			const msgsContainer = document.getElementsByClassName("messages")[0];
			msgsContainer.scrollTop = msgsContainer.scrollHeight;
		}
	}, [messages]);

	// Effect hook to determine whether or not a chat participant has been mentioned.
	// Sends a push notification to all mentioned users via the RollKall API
	useEffect(() => {
		if (mentionMessage) {
			let mentions = [];
			if (mentionMessage.includes("@here")) {
				// eslint-disable-next-line react/destructuring-assignment
				mentions = Object.entries(participants).filter(
					([Id]) => Id !== props.userId.toString(),
				);
			} else {
				mentions = Object.entries(participants).filter(([, { FullName }]) =>
					mentionMessage.includes(`@${FullName}`),
				);
			}
			mentions.forEach(([UserId]) => {
				const data = {
					UserId,
					// eslint-disable-next-line react/destructuring-assignment
					Title: participants[props.userId]?.FullName || "New Message",
					Message: mentionMessage,
					// eslint-disable-next-line react/destructuring-assignment
					Icon: participants[props.userId]?.ContactPhotoUrl || "",
					ClickAction: `/jobs/${activeChat}#openChat`,
					// eslint-disable-next-line react/destructuring-assignment
					Parameters: {
						"rk-jobEventId": activeChat,
						image: participants[props.userId]?.ContactPhotoUrl || "",
					},
				};

				ChatService.mentionUser(data).catch(() => {});
			});
			setMentionMessage();
		}
	}, [mentionMessage, participants]);

	// Effect hook used to toggle the mentionable users list when the user inputs an '@' symbol.
	// Based on what text appears after the '@', if any, the list of mentionable users is filtered to narrow down the options shown to the user
	useEffect(() => {
		const newMention = /^.*@\S{0}$/g.test(input);
		const filteredMention = /^.*@\S+$/g.test(input);
		const noMention = /^.*(@\s+).*$/g.test(input) || /^[^@]*$/g.test(input);
		const splitMention = input.split("@");
		if (newMention) {
			// show all available mentions
			setShowMentions("all");
		} else if (
			noMention ||
			(filteredMention &&
				filterJobParticipants(splitMention[splitMention.length - 1]).length === 0 &&
				!"here".includes(splitMention[splitMention.length - 1]))
		) {
			// the filteredMention part needs work, but if the user is not trying to @here, hide the mentions window
			setShowMentions("none");
		} else if (filteredMention) {
			// filter mentions based on what they type
			setShowMentions(input.match(/@\S+$/g)[0].replace("@", ""));
		}
	}, [input]);

	// Effect hook to set the first user in the mentionable users list to the "active" user. When the list is hidden, this hook
	// sets the focus back to the input so that the user can continue typing normally.
	useEffect(() => {
		// Remove the active class from the currently selected user
		if (document.querySelector(".mentions > div.active")) {
			document.querySelector(".mentions > div.active").classList.remove("active");
		}
		if (showMentions !== "none") {
			// set the first option as the new active option
			const first = document.querySelector(".mentions > div");
			if (first) first.classList.add("active");
		} else if (inputRef.current) {
			inputRef.current.focus();
		}
	}, [showMentions]);

	return (
		<div
			id="job-chat"
			className={collapsed ? "collapsed-chat" : "container-fluid"}
		>
			<Choose>
				<When condition={collapsed}>
					<div
						role="button"
						tabIndex="0"
						onClick={() => setCollapsed(!collapsed)}
						onKeyPress={({ key }) => {
							if (key === "Enter") setCollapsed(!collapsed);
						}}
					>
						<Image
							src={ChatIcon}
							alt="Job Chats"
						/>
						<span
							className={classNames("badge", { hidden: unreadChannels.length === 0 })}
						>
							&nbsp;
							{/* Without something inside the badge, bootstrap sets display: none based on .badge:empty */}
						</span>
					</div>
				</When>
				<Otherwise>
					<div className="row chat-header">
						<div className="col-xs-3 col-sm-2">
							<If condition={activeChat}>
								<button
									type="button"
									aria-label="View all chats"
									className="btn btn-link btn-block text-left"
									onClick={() => setActiveChat()}
								>
									<i className="glyphicon glyphicon-menu-left" />
								</button>
							</If>
						</div>
						<div className="col-xs-6 col-sm-8 text-center">
							<strong>{activeChat ? `Job #${activeChat}` : "Job Chat"}</strong>
						</div>
						<div className="col-xs-3 col-sm-2">
							<button
								type="button"
								aria-label="Minimize chat window"
								className="btn btn-link btn-block text-right"
								onClick={() => setCollapsed(!collapsed)}
							>
								<i className="glyphicon glyphicon-remove" />
							</button>
						</div>
					</div>
					<Choose>
						<When condition={activeChat}>
							<div className="row messages">
								<Choose>
									<When condition={loadingChat}>
										<div className="col-sm-12 text-center">
											<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
										</div>
									</When>
									<When condition={messages.length === 0}>
										<div className="text-center text-muted">
											No messages to show
										</div>
									</When>
								</Choose>
								{/* Using .map instead of <For> for ability to destructure needed values from each message */}
								{messages.map(({ message, publisher }) => (
									<div
										key={`${publisher}_${message.timestamp}`}
										className="col-sm-12"
									>
										<div className="row">
											<Choose>
												<When condition={message.isError}>
													<div className="text-danger col-xs-10 col-sm-9 col-xs-offset-2 col-sm-offset-3">
														<div>
															<i className="glyphicon glyphicon-exclamation-sign" />
															&nbsp;
															{message.text}
														</div>
														<small className="text-muted">
															{utils.formatTime(
																new Date(message.timestamp),
																true,
															)}
														</small>
													</div>
												</When>
												<Otherwise>
													<div className="col-xs-2 col-sm-3">
														<Image
															fallbackSrc="avatar"
															className="img-circle img-responsive"
															src={
																participants[publisher]
																	?.ContactPhotoUrl
															}
														/>
													</div>
													<div className="nopad-left col-xs-10 col-sm-9">
														<small className="text-capitalize">
															<button
																type="button"
																className="btn btn-link"
																onClick={() =>
																	handleUsernameClick(
																		input,
																		participants[publisher]
																			?.FullName,
																	)
																}
															>
																<strong>
																	{participants[publisher]
																		?.FullName ||
																		`User ${publisher}`}
																</strong>
															</button>
															<Choose>
																<When
																	condition={
																		participants[publisher]
																			?.IsAgencySupervisor
																	}
																>
																	<span className="label label-primary text-capitalize">
																		{
																			participants[publisher]
																				.PostionTitle
																		}
																		{/* API currently misspelled PositionTitle, will correct once fixed */}
																	</span>
																</When>
																<When
																	condition={
																		participants[publisher]
																	}
																>
																	<span className="label label-default text-capitalize">
																		{
																			participants[publisher]
																				.PostionTitle
																		}
																	</span>
																</When>
															</Choose>
														</small>
														<div className="message-content">
															{renderMessageText(
																message.text,
																participants,
															)}
														</div>
														<small className="text-muted">
															{utils.formatTime(
																new Date(message.timestamp),
																true,
															)}
														</small>
													</div>
												</Otherwise>
											</Choose>
										</div>
									</div>
								))}
							</div>
							<div className="row new-message">
								<div
									className={classNames("mentions", {
										open: showMentions !== "none",
									})}
								>
									<Choose>
										<When condition={showMentions === "all"}>
											<div
												role="button"
												tabIndex="0"
												className="col-sm-12"
												onMouseOver={handleMentionHover}
												onFocus={handleMentionHover}
												onClick={() => handleMentionClick(input, "here")}
												onKeyPress={null}
											>
												@here - <em>Notify all job participants</em>
											</div>
											{Object.entries(participants).map(
												([userId, user], index) => (
													<div
														key={`mention-${userId}`}
														role="button"
														tabIndex={index + 1}
														className="col-sm-12"
														onMouseOver={handleMentionHover}
														onFocus={handleMentionHover}
														onClick={() =>
															handleMentionClick(input, user.FullName)
														}
														onKeyPress={null}
													>
														{user.FullName}
													</div>
												),
											)}
										</When>
										<When condition={showMentions !== "none"}>
											<div
												role="button"
												tabIndex="0"
												className="col-sm-12"
												onMouseOver={handleMentionHover}
												onFocus={handleMentionHover}
												onClick={() => handleMentionClick(input, "here")}
												onKeyPress={null}
											>
												@here - <em>Notify all job participants</em>
											</div>
											{filterJobParticipants(showMentions).map(
												([userId, user], index) => (
													<div
														key={`mention-${userId}`}
														role="button"
														tabIndex={index + 1}
														className="col-sm-12"
														onMouseOver={handleMentionHover}
														onFocus={handleMentionHover}
														onClick={() =>
															handleMentionClick(input, user.FullName)
														}
														onKeyPress={null}
													>
														{user.FullName}
													</div>
												),
											)}
										</When>
									</Choose>
								</div>
								<div className="col-sm-12">
									<div className="input-group">
										<input
											ref={inputRef}
											className="form-control"
											placeholder="Type your message..."
											value={input}
											onChange={({ target: { value } }) => setInput(value)}
											onKeyDown={event => handleInputKeydown(event)}
											onKeyPress={({ key }) => {
												if (key === "Enter" && input !== "") sendMessage();
											}}
										/>
										<span className="input-group-btn">
											<button
												type="button"
												className="btn btn-primary"
												disabled={input === ""}
												onClick={sendMessage}
											>
												<i className="glyphicon glyphicon-arrow-up" />
											</button>
										</span>
									</div>
								</div>
							</div>
						</When>
						<Otherwise>
							<div
								className={classNames("row push-notif-banner", {
									dismissed: !showNotifBanner,
									hidden: FirebaseService.messaging === null,
								})}
							>
								<div className="col-xs-2">
									<button
										type="button"
										className="btn btn-link btn-block"
										onClick={dismissNotifBanner}
									>
										&times;
									</button>
								</div>
								<div className="col-sm-12">
									Receive push notifications when mentioned
								</div>
								<div className="col-xs-4 col-xs-offset-8">
									<button
										type="button"
										className="btn btn-default btn-xs btn-block"
										onClick={enableNotifications}
									>
										Enable
									</button>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<strong>Active Chats</strong>
								</div>
							</div>
							<div className="active-chats-container">
								<For
									each="channel"
									of={channels}
								>
									<div
										key={`job-chat-${channel.id}`}
										className="row channel"
									>
										<div className="col-xs-2 exit-container">
											<button
												type="button"
												className="btn btn-link btn-block"
												onClick={() => exitChannel(channel.id)}
											>
												<i className="glyphicon glyphicon-remove" />
											</button>
										</div>
										<div
											role="button"
											tabIndex="0"
											className="col-xs-9"
											onClick={() => setActiveChat(channel.id)}
											onKeyPress={event => {
												if (event.key === "Enter")
													setActiveChat(channel.id);
											}}
										>
											<div className="pull-left">
												{channel.id}
												<If condition={unreadChannels.includes(channel.id)}>
													<span className="badge">&nbsp;</span>
												</If>
												<br />
												<small className="text-muted">{channel.name}</small>
											</div>
											<i className="glyphicon glyphicon-menu-right pull-right" />
										</div>
									</div>
								</For>
							</div>
						</Otherwise>
					</Choose>
				</Otherwise>
			</Choose>
		</div>
	);
};

JobChat.propTypes = { userId: PropTypes.number.isRequired };

export default JobChat;
